import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Users, Clock, MapPin, Swords, Heart, UserPlus, Lock, Globe, MessageCircle, Trophy, Link2 } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import MatchChat from "@/components/MatchChat";
import MatchResultDialog from "@/components/MatchResultDialog";


export default function MatchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMatch = async () => {
    try {
      const res = await api.get(`/matches/${id}`);
      setMatch(res.data);
    } catch {
      toast.error("Match not found");
      navigate("/matches");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMatch(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleJoin = async () => {
    try {
      await api.post(`/matches/${id}/join`);
      toast.success("Joined match!");
      fetchMatch();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-coral rounded-full border-t-transparent" />
      </div>
    );
  }

  if (!match) return null;

  const isFull = match.players?.length >= match.max_players;
  const hasJoined = match.players?.some(p => p.id === user?.id);
  const isCreator = match.creator_id === user?.id;
  const isPrivate = match.visibility === "private";

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        {/* Back button */}
        <Button
          variant="ghost"
          size="sm"
          className="mb-4 rounded-full gap-1.5 text-muted-foreground hover:text-foreground"
          onClick={() => navigate("/matches")}
          data-testid="match-detail-back"
        >
          <ArrowLeft size={16} /> Back to Matches
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Match Info - Left */}
          <div className="lg:col-span-2 space-y-4">
            <Card className="border-border/50" data-testid="match-detail-card">
              <CardContent className="p-5 space-y-4">
                {/* Header badges */}
                <div className="flex flex-wrap gap-2">
                  <Badge className={`rounded-full text-xs ${match.mode === "competitive" ? "bg-coral/10 text-coral border-coral/30" : "bg-teal/10 text-teal border-teal/30"}`}>
                    {match.mode === "competitive" ? <Swords size={12} className="mr-1" /> : <Heart size={12} className="mr-1" />}
                    {match.mode}
                  </Badge>
                  <Badge className={`rounded-full text-xs ${isPrivate ? "bg-golden/10 text-golden border-golden/30" : "bg-teal/10 text-teal border-teal/30"}`}>
                    {isPrivate ? <Lock size={12} className="mr-1" /> : <Globe size={12} className="mr-1" />}
                    {isPrivate ? "Private" : "Open"}
                  </Badge>
                  {match.status === "completed" && (
                    <Badge className="rounded-full text-xs bg-green-500/10 text-green-500 border-green-500/30">Completed</Badge>
                  )}
                </div>

                {/* Club name */}
                <h1 className="font-heading font-bold text-xl">{match.club_name || "TBD"}</h1>

                {/* Details */}
                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Clock size={15} className="text-coral" />
                    <span>{match.date} at {match.time}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users size={15} className="text-teal" />
                    <span>{match.players?.length || 0}/{match.max_players} players</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin size={15} className="text-golden" />
                    <span>{match.skill_level === "all" ? "All Levels" : match.skill_level}</span>
                  </div>
                </div>

                {/* Players list */}
                <div>
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Players</h3>
                  <div className="space-y-2">
                    {match.players?.map((p, i) => (
                      <div key={i} className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-bold border-2 border-background">
                          {p.name?.[0]?.toUpperCase()}
                        </div>
                        <p className="text-sm font-medium leading-tight">{p.name}</p>
                        {p.id === match.creator_id && (
                          <Badge variant="secondary" className="text-[10px] rounded-full ml-auto">Host</Badge>
                        )}
                      </div>
                    ))}
                    {Array.from({ length: match.max_players - (match.players?.length || 0) }).map((_, i) => (
                      <div key={`empty-${i}`} className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full border-2 border-dashed border-border" />
                        <span className="text-sm text-muted-foreground/50">Waiting...</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                {user && !hasJoined && !isFull && match.status !== "completed" && (
                  <Button
                    onClick={handleJoin}
                    className="w-full rounded-full bg-teal hover:bg-teal-600 text-white gap-1.5"
                    data-testid="match-detail-join-btn"
                  >
                    <UserPlus size={16} /> Join Match
                  </Button>
                )}
                {hasJoined && match.status !== "completed" && (
                  <div className="space-y-2">
                    <Badge className="w-full justify-center py-2.5 rounded-full bg-teal/10 text-teal border-teal/30 text-sm">
                      You're in!
                    </Badge>
                    {isPrivate && isCreator && (
                      <Button
                        variant="outline"
                        className="w-full rounded-full gap-1.5 text-sm border-golden/30 text-golden hover:bg-golden/10"
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/matches/${id}`);
                          toast.success("Invite link copied! Share it with friends.");
                        }}
                        data-testid="match-detail-share-link"
                      >
                        <Link2 size={14} /> Copy Invite Link
                      </Button>
                    )}
                  </div>
                )}
                {isFull && match.status !== "completed" && (
                  <MatchResultDialog match={match} onSubmitted={fetchMatch} />
                )}
                {match.status === "completed" && match.result && (
                  <div className="space-y-2">
                    <div className="p-3 rounded-xl bg-golden/5 border border-golden/15 text-center">
                      <p className="text-xs font-semibold text-golden mb-1">Final Score</p>
                      <p className="text-2xl font-black">{match.result.team1_score} - {match.result.team2_score}</p>
                    </div>
                    <Button
                      variant="outline"
                      className="w-full rounded-full gap-1.5 text-sm"
                      onClick={() => navigate(`/matches/${id}/highlights`)}
                      data-testid="view-highlights-btn"
                    >
                      <Trophy size={14} className="text-golden" /> View Highlights & Share
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Chat - Right */}
          <div className="lg:col-span-3">
            <div className="flex items-center gap-2 mb-3">
              <MessageCircle size={18} className="text-coral" />
              <h2 className="font-heading font-bold text-lg">Group Chat</h2>
              {match.visibility === "open" && (
                <span className="text-[10px] text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-full">Open to all</span>
              )}
            </div>
            <MatchChat matchId={id} match={match} />
          </div>
        </div>
      </motion.div>
    </div>
  );
}
